/**
 * Helpers for the post-auth `redirect` query param.
 *
 * The param carries the route a user was trying to reach before they were
 * bounced into the auth flow. Landing-page links such as
 * app.stagepanda.com/checkout/presenter depend on it surviving the whole
 * chain: /login -> (new users) /onboarding -> the original destination.
 */

export const REDIRECT_PARAM = "redirect";

// Routes that are part of the auth flow itself — using one as a redirect target
// would bounce the user straight back into the flow they just finished.
const NON_REDIRECTABLE_PATHS = ["/login", "/onboarding"];

export const getSafeRedirectPath = (value?: string | null): string | null => {
  if (!value) return null;

  let decoded = value.trim();
  // Defensively decode multiple times to support nested redirect encoding
  // patterns that can happen through chained loader redirects.
  for (let i = 0; i < 3; i += 1) {
    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next;
    } catch {
      break;
    }
  }

  // Only allow internal relative routes to prevent open redirects.
  if (!decoded.startsWith("/") || decoded.startsWith("//")) {
    return null;
  }

  return decoded;
};

export const resolveFinalRedirectPath = (
  value?: string | null,
): string | null => {
  let current = getSafeRedirectPath(value);
  if (!current) return null;

  // Unwrap nested redirect chains like:
  // /login?redirect=%2Flogin%3Fredirect%3D%252Fmeet-addon%252Fmain-stage...
  for (let i = 0; i < 5; i += 1) {
    try {
      const parsed = new URL(current, window.location.origin);
      const isLoginRoute = parsed.pathname === "/login";
      const nested = parsed.searchParams.get(REDIRECT_PARAM);
      if (!isLoginRoute || !nested) break;

      const next = getSafeRedirectPath(nested);
      if (!next || next === current) break;
      current = next;
    } catch {
      break;
    }
  }

  try {
    const { pathname } = new URL(current, window.location.origin);
    if (NON_REDIRECTABLE_PATHS.includes(pathname)) return null;
  } catch {
    return null;
  }

  return current;
};

/**
 * Appends `?redirect=<target>` to a route, preserving any params it already has.
 * Returns `path` untouched when there is no safe target to carry forward.
 */
export const withRedirectParam = (
  path: string,
  redirectTo?: string | null,
): string => {
  const safeTarget = resolveFinalRedirectPath(redirectTo);
  if (!safeTarget) return path;

  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}${REDIRECT_PARAM}=${encodeURIComponent(safeTarget)}`;
};

/** Reads the redirect target off the current browser URL. */
export const readRedirectParam = (): string | null => {
  try {
    const params = new URLSearchParams(window.location.search);
    return resolveFinalRedirectPath(params.get(REDIRECT_PARAM));
  } catch {
    return null;
  }
};
